import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../../../api/client";
import { fmtDay, fmtRange } from "../../home/time";
import Icon from "../Icon";
import { Pill } from "./staffUi";

const errMsg = (e, fallback) => e?.response?.data?.message || fallback;
const dayLabel = (ymd, add = 0) => { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + add); return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }); };
const REC = {
  posted: ["ok", "Posted to the lesson"], processing: ["brand", "Vimeo is processing"], importing: ["brand", "Importing from Zoom"],
  waiting: ["gray", "Waiting for Zoom to finish"], failed: ["risk", "Failed"],
};

/**
 * Start until someone has started the class, then Join (a normal participant link; the host can make
 * them co-host in Zoom). `note` says who started it.
 */
export function classAction(c) {
  if (c.started) return { kind: "join", label: "Join class", enabled: c.can_join, note: c.started_by ? `Started by ${c.started_by}` : "Class started" };
  return { kind: "start", label: "Start class", enabled: c.can_start, note: null };
}

/** Opens a link the server hands back (Start / Join), in a new tab. */
export async function openClass(id, kind) {
  const tab = window.open("", "_blank");
  try {
    const r = await client.post(`/staff/live/${id}/${kind}`);
    if (tab) tab.location.href = r.data.url; else window.open(r.data.url, "_blank", "noopener,noreferrer");
    if (r.data.message) toast.info(r.data.message); // e.g. someone else started it first: joined as a participant
  } catch (e) {
    tab?.close();
    toast.error(errMsg(e, "We couldn't open the class."));
  }
}

function Hosts() {
  const [h, setH] = useState(null);
  const [custom, setCustom] = useState({});
  const load = () => client.get("/staff/live/hosts").then((r) => setH(r.data)).catch(() => setH(false));
  useEffect(() => { load(); }, []);
  const save = async (c, email) => {
    try { const r = await client.put("/staff/live/hosts", { cohort_id: c.cohort_id, course_id: c.course_id, host_email: email || null }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(errMsg(e, "We couldn't save the host.")); }
  };
  if (h === null) return <div className="skeleton" style={{ height: 160 }} />;
  if (h === false) return null;
  const name = (c) => (c.cohort.startsWith(c.course) ? c.cohort : `${c.course} · ${c.cohort}`);
  return (
    <div className="sp-two">
      <section className="sp-box">
        <div className="sp-box-h"><h2>Which Zoom host runs each course</h2></div>
        <div className="sp-table-wrap"><table className="sp-table">
          <thead><tr><th>Course</th><th>Zoom host</th></tr></thead>
          <tbody>{h.courses.map((c) => {
            const key = `${c.cohort_id}-${c.course_id}`;
            return (
              <tr key={key}>
                <td><strong style={{ color: "var(--ink)", fontWeight: 500 }}>{name(c)}</strong></td>
                <td>{h.users.length ? (
                  <select className="sp-select" aria-label={`Zoom host for ${name(c)}`} value={c.host || ""} onChange={(e) => save(c, e.target.value)} disabled={!h.zoom}>
                    <option value="">Not on Zoom (paste links by hand)</option>
                    {h.users.map((u) => <option key={u.email} value={u.email}>{u.email}{u.name ? ` · ${u.name}` : ""}</option>)}
                    {c.host && !h.users.some((u) => u.email === c.host) && <option value={c.host}>{c.host}</option>}
                  </select>
                ) : (
                  <span className="sp-acts">
                    <input className="sp-select" style={{ minWidth: 220 }} aria-label={`Zoom host email for ${name(c)}`} placeholder={h.zoom ? "host@e1xacademy.com" : "Connect Zoom first"} disabled={!h.zoom}
                      value={custom[key] ?? c.host ?? ""} onChange={(e) => setCustom({ ...custom, [key]: e.target.value })} />
                    {h.zoom && (custom[key] ?? c.host ?? "") !== (c.host ?? "") && <button className="cbtn quiet" type="button" onClick={() => save(c, custom[key])}>Save</button>}
                  </span>
                )}</td>
              </tr>
            );
          })}</tbody>
        </table></div>
        <div className="sp-foot"><span>Each Pro licence hosts one meeting at a time, so courses whose classes overlap need different hosts. Changing a host moves the course's upcoming meetings to them.</span></div>
      </section>
      <section className="sp-box">
        <div className="sp-box-h"><h2>Connections</h2></div>
        <div className="sp-rows">
          <div><span className={`sp-fico ${h.zoom && !h.error ? "ok" : ""}`}><Icon name={h.zoom && !h.error ? "check" : "alert"} size={16} /></span>
            <div><strong>Zoom</strong><small>{!h.zoom ? "Not connected. Add the ZOOM_* settings on the server." : h.error ? h.error : `${h.users.length} licensed host${h.users.length === 1 ? "" : "s"}`}</small></div><span /></div>
          <div><span className={`sp-fico ${h.vimeo ? "ok" : ""}`}><Icon name={h.vimeo ? "check" : "alert"} size={16} /></span>
            <div><strong>Vimeo</strong><small>{h.vimeo ? "Connected. Recordings post themselves." : "Not connected. Add VIMEO_ACCESS_TOKEN on the server."}</small></div><span /></div>
        </div>
        <div className="sp-foot"><span>Setup steps: docs/LIVE_CLASSES_ZOOM_VIMEO.md in the backend repo.</span></div>
      </section>
    </div>
  );
}

/** Admin: this week's live classes (Start as host), recordings on their way to Vimeo, and Zoom hosts. Week by week. */
export default function StaffLivePage() {
  const [week, setWeek] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    client.get("/staff/live", { params: { week } }).then((r) => { setData(r.data); setError(false); }).catch(() => setError(true));
  }, [week]);
  useEffect(() => { setData(null); load(); }, [load]);
  // Keep Start/Join buttons current as classes come into their 30-minute window.
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const retry = async (c) => {
    try { const r = await client.post(`/staff/live/${c.id}/retry`); toast.success(r.data.message); load(); } catch (e) { toast.error(errMsg(e, "We couldn't retry the import.")); }
  };
  const classes = data?.classes || [];
  const recs = classes.filter((c) => c.recording.status);

  return (
    <div className="lms"><div className="main single" style={{ maxWidth: 1240 }}><div className="view">
      <div className="sp-top">
        <div><span className="sp-kicker">Learning</span><h1>Live classes (Zoom)</h1>
          <p>Every class gets its Zoom meeting automatically. Start classes here as the host (instructors can also start their own classes from their dashboard); recordings post themselves.</p></div>
      </div>
      <div className="sp-wknav">
        <button className="cbtn quiet" type="button" onClick={() => setWeek(week - 1)} aria-label="Previous week">‹</button>
        <div><b>{week === 0 ? "This week" : week === -1 ? "Last week" : week === 1 ? "Next week" : `Week of ${data ? dayLabel(data.week.from) : "…"}`}</b><small>{data ? `${dayLabel(data.week.from)} – ${dayLabel(data.week.from, 6)} · ${classes.length} ${classes.length === 1 ? "class" : "classes"}` : "Loading…"}</small></div>
        <button className="cbtn quiet" type="button" onClick={() => setWeek(week + 1)} aria-label="Next week">›</button>
        {week !== 0 && <button className="link-btn" type="button" onClick={() => setWeek(0)}>Back to this week</button>}
      </div>

      {error ? <section className="complete-box"><div><strong>We couldn't load the classes</strong><small>Check your connection and try again.</small></div><button className="cbtn quiet" type="button" onClick={load}>Try again</button></section>
        : !data ? <div className="skeleton" style={{ height: 260 }} />
        : (
          <>
            <section className="sp-box">
              <div className="sp-box-h"><h2>Live classes</h2><small>Your time zone</small></div>
              <div className="sp-rows">
                {classes.map((c) => (
                  <div key={c.id}>
                    <span className={`sp-fico big ${c.state === "ended" ? "ok" : ""}`}><Icon name="video" /></span>
                    <div><strong>{c.topic}{c.makeup ? " · makeup" : ""}</strong><small>{c.cohort.startsWith(c.course) ? c.cohort : `${c.course} · ${c.cohort}`} · {fmtDay(c.start)}, {fmtRange(c.start, c.end)}{!c.zoom && !c.has_link ? " · no link yet" : !c.zoom ? " · manual link" : ""}</small></div>
                    <span className="sp-acts">
                      {c.state === "ended" ? <Pill cls="gray">Ended</Pill> : c.state === "live" ? <Pill cls="ok">Live now</Pill> : null}
                      {(() => {
                        const a = classAction(c);
                        if (a.enabled) return <>{a.note && <span className="course-tag" style={{ fontSize: 12.5 }}>{a.note}</span>}<button className="cbtn go" type="button" onClick={() => openClass(c.id, a.kind)}>{a.label}</button></>;
                        return c.state !== "ended" && <><span className="course-tag" style={{ fontSize: 12.5 }}>Start opens 30 min before</span><button className="cbtn quiet" type="button" disabled>Start class</button></>;
                      })()}
                    </span>
                  </div>
                ))}
                {!classes.length && <div><span /><small>No classes this week.</small><span /></div>}
              </div>
            </section>

            <section className="sp-box">
              <div className="sp-box-h"><h2>Recordings</h2><small>Same week · Zoom cloud → Vimeo → lesson{data.vimeo ? "" : " · Vimeo not connected yet"}</small></div>
              <div className="sp-pipe">{["Class ends", "Zoom finishes the cloud recording", "Vimeo imports it from Zoom", "Vimeo processes it", "Link added to the class · students notified"].map((x, i) => <span key={x}><b>{i + 1}</b>{x}</span>)}</div>
              <div className="sp-table-wrap"><table className="sp-table">
                <thead><tr><th>Class</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {recs.map((c) => (
                    <tr key={c.id}>
                      <td><strong style={{ color: "var(--ink)", fontWeight: 500 }}>{c.topic}</strong><div className="course-tag" style={{ fontSize: 12.5 }}>{c.course} · {fmtDay(c.start)}</div></td>
                      <td><Pill cls={REC[c.recording.status]?.[0] || "gray"}>{REC[c.recording.status]?.[1] || c.recording.status}</Pill>{c.recording.error && <div className="course-tag" style={{ fontSize: 12.5 }}>{c.recording.error}</div>}</td>
                      <td className="num">{c.recording.status === "posted" ? <a className="link-btn" href={c.recording.link} target="_blank" rel="noopener noreferrer">Open</a>
                        : c.recording.status === "failed" ? <span className="sp-acts" style={{ justifyContent: "flex-end" }}>{c.zoom && <button className="cbtn quiet" type="button" onClick={() => retry(c)}>Retry</button>}<span className="course-tag" style={{ fontSize: 12.5 }}>or paste a link in the week editor</span></span> : null}</td>
                    </tr>
                  ))}
                  {!recs.length && <tr><td colSpan={3} className="course-tag">{week > 0 ? "Recordings appear here after each class." : "No recordings from this week yet."}</td></tr>}
                </tbody>
              </table></div>
            </section>
          </>
        )}
      <Hosts />
    </div></div></div>
  );
}
